import React from 'react';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

interface DashboardPanelProps {
  children: React.ReactNode;
  title?: string;
  side: 'left' | 'right' | 'bottom' | 'center';
  isOpen: boolean;
  onToggle: () => void;
  width?: number | string;
  height?: number | string;
  maxHeight?: number | string;
  autoOpen?: boolean;
  companionButtons?: React.ReactNode;
}

const DashboardPanel: React.FC<DashboardPanelProps> = ({ 
  children, title, side, isOpen, onToggle, width = 320, height, maxHeight = 'calc(100vh - 120px)', companionButtons
}) => {
  const isHorizontal = side === 'left' || side === 'right';
  
  const getPanelStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'rgba(10, 10, 20, 0.75)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(0, 243, 255, 0.2)',
      boxShadow: '0 0 30px rgba(0, 0, 0, 0.5)',
      overflow: 'hidden',
      pointerEvents: isOpen ? 'auto' : 'none',
      visibility: (side === 'center' && !isOpen) ? 'hidden' : 'visible',
    };

    if (side === 'left') {
      return {
        ...base,
        top: 80,
        left: isOpen ? 16 : -Number(width) - 40,
        width: width,
        height: height || 'auto',
        maxHeight: maxHeight,
        opacity: isOpen ? 1 : 0,
      };
    }
    if (side === 'right') {
      return {
        ...base,
        top: 80,
        right: isOpen ? 16 : -Number(width) - 40,
        width: width,
        height: height || 'auto',
        maxHeight: maxHeight,
        opacity: isOpen ? 1 : 0,
      };
    }
    if (side === 'bottom') {
      return {
        ...base,
        bottom: isOpen ? 80 : -500,
        left: '50%',
        transform: 'translateX(-50%)',
        width: width || '80%',
        maxHeight: '40vh',
      };
    }
    if (side === 'center') {
      return {
        ...base,
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) scale(${isOpen ? 1 : 0.8})`,
        opacity: isOpen ? 1 : 0,
        width: width,
        height: height,
        zIndex: 20,
      };
    }
    return base;
  };

  const getToggleButtonContainerStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      zIndex: 11,
      pointerEvents: 'none',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    };

    if (side === 'left') {
      return { ...base, top: 80, left: isOpen ? Number(width) + 24 : 16 };
    }
    if (side === 'right') {
      return { ...base, top: 80, right: isOpen ? Number(width) + 24 : 16 };
    }
    return base;
  };

  const buttonBaseStyle: React.CSSProperties = {
    backgroundColor: 'rgba(0, 243, 255, 0.1)',
    border: '1px solid rgba(0, 243, 255, 0.3)',
    backdropFilter: 'blur(4px)',
    color: '#00f3ff',
    pointerEvents: 'auto',
  };

  return (
    <>
      {isHorizontal && (
        <div style={getToggleButtonContainerStyle()}>
            <Stack spacing={1}>
                <IconButton size="small" onClick={onToggle} style={buttonBaseStyle}>
                    {side === 'left' ? (isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />) : (isOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />)}
                </IconButton>
                {companionButtons}
            </Stack>
        </div>
      )}
      
      <div style={getPanelStyle()}>
        {title && (
          <div style={{ 
            padding: '12px', 
            borderBottom: '1px solid rgba(0, 243, 255, 0.1)', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: 'linear-gradient(90deg, rgba(0, 243, 255, 0.1) 0%, transparent 100%)'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'primary.main', textTransform: 'uppercase' }}>
              {title}
            </Typography>
            {side === 'center' && (
              <IconButton size="small" onClick={onToggle} sx={{ color: 'primary.main' }}>
                <CloseFullscreenIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        )}
        <div style={{ flexGrow: 1, overflowY: 'auto', padding: '16px' }}>
          {isOpen && children}
        </div>
      </div>
    </>
  );
};

export default DashboardPanel;
